import Address from 'components/Address'
import HeaderControls from 'components/InfinityTable/HeaderControls'
import ListContainer from 'components/InfinityTable/ListContainer'
import RowControls from 'components/InfinityTable/RowControls'
import Sort from 'components/InfinityTable/Sort'
import AddressModal from 'containers/AddressModal'
import Import from 'containers/AddressModal/Import'
import lodash from 'lodash'
import React, { useEffect } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const HeaderRow = styled.div`
  font-weight: bold;
  text-transform: uppercase;
`

const AddressBookNew = (props) => {
  const { setAddressModal, fullWidth, onApplyAddress } = props

  const { t } = useTranslation()

  useEffect(() => {
    props.applyFilter({ reset: true, onlyFts: false })
  }, [])

  const importAddresses = () => {
    setAddressModal({
      address: undefined,
      error: undefined,
      result: undefined,
      isOpen: true,
      isDelete: false,
      isImport: true,
    })
  }

  const exportAddressbook = () => {
    const form = document.createElement('form')
    form.setAttribute('method', 'get')
    form.setAttribute('target', '_blank')
    form.setAttribute('action', '/addressbook/download/csv')
    document.body.appendChild(form)
    form.submit()
    form.remove()
  }

  const createAddress = () => {
    setAddressModal({
      address: undefined,
      error: undefined,
      result: undefined,
      isOpen: true,
      isDelete: false,
      isImport: false,
    })
  }
  const deleteAddress = (index, e) => {
    if (e) {
      e.stopPropagation()
    }
    const { setAddressModal, items } = props
    setAddressModal({
      address: items[index],
      error: undefined,
      result: undefined,
      isOpen: true,
      isDelete: true,
      isImport: false,
    })
  }

  const editAddress = (index, e) => {
    if (e) {
      e.stopPropagation()
    }
    const { setAddressModal, items } = props
    setAddressModal({
      address: items[index],
      error: undefined,
      result: undefined,
      isOpen: true,
      isDelete: false,
      isImport: false,
    })
  }

  const headerControls = (
    <HeaderControls
      controls={[
        {
          label: i18n.t('add', 'Hinzufügen'),
          icon: 'far fa-plus',
          onClick: createAddress,
        },
        {
          label: 'CSV-Import',
          icon: 'far fa-plus',
          onClick: importAddresses,
        },
        {
          label: 'CSV-Export',
          icon: 'fas fa-download',
          onClick: exportAddressbook,
        },
      ]}
    />
  )

  const rowControls = (rowProps) => {
    return (
      <RowControls
        {...rowProps}
        controls={[
          onApplyAddress
            ? {
                label: i18n.t("apply", "Übernehmen"),
                icon: 'fas fa-check',
                variant: 'primary',
                onClick: () =>
                  onApplyAddress(lodash.get(rowProps, 'rowData', {})),
              }
            : undefined,
          {
            label: i18n.t('edit', 'Bearbeiten'),
            icon: 'fas fa-pencil',
            variant: 'light',
            onClick: () => editAddress(lodash.get(rowProps, 'rowIndex')),
          },
          {
            label: i18n.t('delete', 'Löschen'),
            icon: 'fas fa-trash-alt',
            variant: 'danger',
            onClick: () => deleteAddress(lodash.get(rowProps, 'rowIndex')),
          },
        ].filter(Boolean)}
      />
    )
  }

  const mobileColumn = [
    {
      headerRenderer: (renderprops) => (
        <HeaderRow className="d-flex justify-content-between align-items-center">
          <Sort
            {...props}
            sorts={props.sort}
            sortKey="id"
            applyFilter={props.applyFilter}
            setSort={props.setSort}
            sortableColumns={props.sortableColumns}
            label="#"
          />
          {headerControls}
        </HeaderRow>
      ),
    },
  ]

  const columns = [
    {
      label: '#',
      dataKey: 'id',
      sortKey: 'id',
      flexGrow: 0,
    },
    {
      label: 'Ref',
      dataKey: 'reference',
      flexGrow: 0,
    },
    {
      label: i18n.t('address', 'Adresse'),
      dataKey: [
        'name1',
        'name2',
        'name3',
        'street',
        'streetNumber',
        'streetNumberAddition',
        'zip',
        'city',
        'country',
      ],
      flexGrow: 1,
      cellRenderer: (props) => {
        const { cellData } = props

        return (
          <div {...props}>
            <Address {...cellData} biggerNames oneLineNames />
          </div>
        )
      },
      cellDataGetter: (props) => {
        const data = []
        props.dataKey.forEach((key) => {
          data[key] = lodash.get(props.rowData, key)
        })

        return data
      },
    },
    {
      label: headerControls,
      dataKey: 'rowIndex',
      flexGrow: 1,
      cellRenderer: (rowProps) => (
        <div className="pr-2">{rowControls(rowProps)}</div>
      ),
    },
  ]

  const filter = [
    {
      name: 'reference',
      label: t('label_reference'),
      type: 'value',
      colWidth: 4,
    },
    {
      name: 'name1',
      label: t('label_name1'),
      type: 'value',
      colWidth: 8,
    },

    {
      name: 'name2',
      label: t('label_name2'),
      type: 'value',
      colWidth: 6,
    },
    {
      name: 'name3',
      label: t('label_name3'),
      type: 'value',
      colWidth: 6,
    },
    {
      name: 'street',
      label: t('label_street'),
      type: 'value',
      colWidth: 6,
    },
    {
      name: 'streetNumber',
      label: t('label_streetNumber'),
      type: 'value',
      colWidth: 3,
    },
    {
      name: 'streetNumberAddition',
      label: t('label_streetNumberAddition'),
      type: 'value',
      colWidth: 3,
    },

    { name: 'zip', label: t('label_plz'), type: 'value', colWidth: 4 },
    {
      name: 'city',
      label: t('label_city'),
      type: 'value',
      colWidth: 4,
    },
    {
      name: 'country',
      label: t('label_country_short'),
      type: 'value',
      colWidth: 4,
    },
    {
      name: 'fulltext',
      label: i18n.t('search_any_field', 'Suche nach beliebigen Feldern (z.B. Name, Straße, etc.)'),
      type: 'fulltext',
      exludeForDetail: true,
      showForFts: true,
      colWidth: 12,
    },
  ]

  const mobileCellHeaderItems = ({ rowData }) => {
    const reference = lodash.get(rowData, 'reference')
    return [
      {
        width: '50px',
        label: lodash.get(rowData, 'id', '?'),
      },
      {
        flex: '1',
        label: lodash.get(rowData, 'name1', ''),
      },
      {
        flex: '1',
        label: reference ? `Referenz: ${reference}` : '',
      },
    ]
  }

  const mobileCellBody = (rowProps) => {
    return (
      <React.Fragment>
        <Row>
          <Col xs="4">
            <span className="font-weight-bold">Referenz:</span>
          </Col>
          <Col xs="8">
            <span>{lodash.get(rowProps.rowData, 'reference', '-')}</span>
          </Col>
        </Row>
        <Row>
          <Col xs="4">
            <span className="font-weight-bold">{i18n.t('address', 'Adresse:')}</span>
          </Col>
          <Col xs="8">
            <span>
              <Address {...rowProps.rowData} oneLineNames />
            </span>
          </Col>
        </Row>
        <div className="p-2">{rowControls(rowProps)}</div>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <AddressModal />
      <Import />
      <ListContainer
        {...props}
        columns={columns}
        filter={filter}
        mobileColumn={mobileColumn}
        rowHeight={80}
        mobileRowHeight={180}
        mobileRowHeightCollapsed={35}
        fullWidth={fullWidth}
        title={i18n.t("addressbook", "Adressbuch")}
        // mobileCellHeader={mobileCellHeader}
        mobileCellHeaderItems={mobileCellHeaderItems}
        mobileCellBody={mobileCellBody}
      />
    </React.Fragment>
  )
}

export default AddressBookNew
